import clsx from 'clsx';
import * as React from 'react';
import { Focusable } from 'react-aria';

import type { BlockProps } from '@gitbook/slate-react';
import { Tooltip } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert, filterOutNullable } from '@gitbook/util-ts';

import { BlockWrapper, useBlockLayoutGroupFactory, useBlockPaletteFactory } from '../common';

// Divisions of the columns layout
export const COLUMN_DIVISIONS = 12;
const LEFT_BOUNDARY = 1;
const RIGHT_BOUNDARY = COLUMN_DIVISIONS - 1;

/*
 * Renderer for columns layout block.
 */
export function BlockColumns(props: BlockProps): React.ReactElement {
    const { children, ...rest } = props;

    const createLayoutGroup = useBlockLayoutGroupFactory({ nodeKey: props.node.key });

    const palette = useBlockPaletteFactory({
        nodeKey: props.node.key,
        createExtraGroups: () => {
            return [createLayoutGroup()].filter(filterOutNullable);
        },
    });

    const columnWidths = React.useMemo(() => {
        const widths = props.node.blockNodes
            .map((block) => {
                const width = block.data.get('width');
                return width ? width.value / 100 : 0;
            })
            .toArray();

        const totalWidth = widths.reduce((acc, width) => acc + width, 0);
        // If not all columns widths are set, distribute the remaining widths as equally as we can
        if (totalWidth < 1.0 && widths.some((width) => width === 0)) {
            const unsetWidths = widths.filter((width) => width === 0);
            let remainingWidth = 1.0 - totalWidth;
            let unsetWidthsLength = unsetWidths.length;
            widths.forEach((width, index) => {
                if (width === 0) {
                    const calculatedWidth =
                        Math.round((remainingWidth / unsetWidthsLength) * COLUMN_DIVISIONS) /
                        COLUMN_DIVISIONS;
                    widths[index] = calculatedWidth; // Assign width to empty columns
                    unsetWidthsLength--;
                    remainingWidth -= calculatedWidth;
                }
            });
        }
        return widths;
    }, [props.node.blockNodes]);

    return (
        <BlockWrapper {...rest} palette={palette}>
            <ColumnWidthProvider value={columnWidths}>
                {!props.editor.readOnly ? (
                    <EditableBlockColumns
                        style={{ '--grid-slices': COLUMN_DIVISIONS } as React.CSSProperties}
                        {...props}
                    >
                        {children}
                    </EditableBlockColumns>
                ) : (
                    <ColumnGrid
                        className="gap-8"
                        style={{ '--grid-slices': COLUMN_DIVISIONS } as React.CSSProperties}
                    >
                        {children}
                    </ColumnGrid>
                )}
            </ColumnWidthProvider>
        </BlockWrapper>
    );
}

function EditableBlockColumns(
    props: BlockProps & { children: React.ReactNode; style?: React.CSSProperties }
) {
    const { children, style } = props;

    const columnWidths = React.useContext(ColumnWidthContext);

    const isCursorInColumns =
        !props.isBlockSelection && props.isSelected && props.isOnlyNodeSelected;

    // Calculate the positions of the resizer
    const positions = React.useMemo(() => {
        const numberOfColumns = props.node.blockNodes.size;
        const arr: number[] = [];
        // Calculate the initial positions based on the widths
        let accumulatedWidth = 0;
        for (let i = 0; i < numberOfColumns - 1; i++) {
            accumulatedWidth += columnWidths[i];
            arr.push(accumulatedWidth);
        }
        return arr;
    }, [props.node.blockNodes, columnWidths]);

    /**
     * Update the width of adjacent columns in columns layout block
     * to take up the new percentage width after resizing.
     */
    const updateColumnWidth = useEventCallback((value: number, index: number) => {
        props.editor.change((change) => {
            const [firstColumn, secondColumn] = props.node.blockNodes.filter(
                (_, i) => i === index || i === index + 1
            );
            assert(
                firstColumn && secondColumn,
                'Each resize handle should have two adjacent columns'
            );
            const primaryWidth = columnWidths[index] * 100;
            const diffWidth = primaryWidth - value * 100;
            change.setDataByKey(firstColumn.key, ['width'], {
                value: value * 100,
                unit: '%',
            });
            const secondaryWidth = columnWidths[index + 1] * 100;
            change.setDataByKey(secondColumn.key, ['width'], {
                value: secondaryWidth + diffWidth,
                unit: '%',
            });
        });
    });

    return (
        <div
            className="overflow-hidden rounded border border-1 border-control border-dashed py-4"
            style={style}
        >
            <ColumnResizer
                positions={positions}
                onSave={updateColumnWidth}
                onUpdate={updateColumnWidth}
                className={clsx(
                    'relative flex flex-col md:flex-row',
                    isCursorInColumns && 'bg-primary/50'
                )}
                disabled={props.isBlockSelection}
                isSelected={isCursorInColumns}
            >
                <ColumnGrid>{children}</ColumnGrid>
            </ColumnResizer>
        </div>
    );
}

export const ColumnWidthContext = React.createContext<number[]>([]);
function ColumnWidthProvider({ children, value }: { children: React.ReactNode; value: number[] }) {
    const contextValue = React.useMemo(() => value, [value]);
    return (
        <ColumnWidthContext.Provider value={contextValue}>{children}</ColumnWidthContext.Provider>
    );
}

function ColumnGrid(props: {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}) {
    return (
        <div
            {...props}
            className={clsx(
                'grid w-full grid-cols-1 gap-y-4 md:grid-flow-col md:grid-cols-[repeat(var(--grid-slices),minmax(0,1fr))] md:grid-rows-1',
                props.className
            )}
        />
    );
}

/**
 * Calculate the width of a single division in the columns layout
 * i.e. the container width divided by the number of divisions.
 */
function calculateDivisionWidth(container?: DOMRect): number {
    const refWidth = container?.width ?? 0;
    const grid = refWidth ? refWidth / COLUMN_DIVISIONS : 1;
    return grid;
}

/**
 * Convert a position offset on the grid (not pixels) to a percentage value
 * e.g offset 1 of 12 returns 0.083333333.
 */
function transformGridPositionToPercentage(offset: number): number {
    return offset / COLUMN_DIVISIONS;
}

/**
 * Convert a position in percentage (0.0833333) to a tooltip label e.g. "1 / 12"
 */
function positionToGridLabel(position: number): string {
    return `${Math.round(position * COLUMN_DIVISIONS)} / ${COLUMN_DIVISIONS}`;
}

/**
 * UI for resizing columns in a columns layout block while editing.
 */
function ColumnResizer(props: {
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
    onSave: (value: number, index: number) => void;
    onUpdate: (value: number, index: number) => void;
    positions: number[];
    isSelected: boolean;
}) {
    const [isDragging, setDragging] = React.useState(false);
    const parentRef = React.useRef<HTMLDivElement>(null);
    const { positions } = props;
    return (
        <div
            ref={parentRef}
            className={clsx(
                props.className,
                'group/resize',
                '[--line-color:var(--sp-color-border-control)] before:px-4',
                // Column resize guidelines
                !props.disabled &&
                    "before:absolute before:inset-0 before:opacity-0 before:transition-opacity before:content-[''] before:[background-clip:content-box] before:[background-image:repeating-linear-gradient(90deg,_var(--sp-color-border-control),_var(--sp-color-border-control)_1px,_transparent_1px,_transparent_calc((1/var(--grid-slices))*100*1%-(1/var(--grid-slices))*1px),_var(--sp-color-border-control)_1px)] before:[background-repeat:no-repeat]",
                isDragging && 'before:opacity-100'
            )}
        >
            {props.children}
            {props.disabled
                ? null
                : positions.map((position, index) => (
                      <ColumnResizeHandle
                          key={`resizer_${index}`}
                          parentRef={parentRef}
                          isSelected={props.isSelected}
                          bounds={{
                              // Calculate the left and right bounds for the resizer
                              left: positions[index - 1]
                                  ? Math.round(positions[index - 1] * COLUMN_DIVISIONS) + 1
                                  : LEFT_BOUNDARY,
                              right: positions[index + 1]
                                  ? Math.round(positions[index + 1] * COLUMN_DIVISIONS) - 1
                                  : RIGHT_BOUNDARY,
                          }}
                          position={`${position * 100}%`}
                          positionLabel={positionToGridLabel(position)}
                          onSave={(value: number, bounds: { left: number; right: number }) => {
                              props.onSave(
                                  value - transformGridPositionToPercentage(bounds.left - 1),
                                  index
                              );
                              setDragging(false);
                          }}
                          onUpdate={(value: number, bounds: { left: number; right: number }) => {
                              props.onUpdate(
                                  value - transformGridPositionToPercentage(bounds.left - 1),
                                  index
                              );
                          }}
                          onStart={() => setDragging(true)}
                      />
                  ))}
        </div>
    );
}

/**
 * Draggable handle for resizing columns in a columns layout block.
 */
const ColumnResizeHandle = function ColumnResizeHandle(props: {
    bounds: {
        left: number;
        right: number;
    };
    isSelected: boolean;
    parentRef: React.RefObject<HTMLDivElement>;
    disabled?: boolean;
    position: React.CSSProperties['left'];
    positionLabel: string;
    onUpdate: (value: number, bounds: { left: number; right: number }) => void;
    onSave: (value: number, bounds: { left: number; right: number }) => void;
    onStart: () => void;
}) {
    const { isSelected, parentRef, position } = props;
    const { ref, isDragging, handlePointerDown, handlePointerMove, handlePointerUp } = useResizer({
        start: useEventCallback(() => {
            if (props.disabled) {
                return;
            }
            props.onStart();
        }),
        transform: useEventCallback((offset: number) => {
            const refRect = parentRef.current?.getBoundingClientRect();
            const refLeft = refRect?.left ?? 0;
            const leftEdge = props.bounds.left;
            const rightEdge = props.bounds.right;
            const grid = calculateDivisionWidth(refRect);
            const deltaX = offset - refLeft;
            const deltaGridX = Math.round(deltaX / grid);
            const adjustedOffset = Math.min(rightEdge, Math.max(leftEdge, deltaGridX)) * grid;
            return adjustedOffset;
        }),
        update: useEventCallback((offset: number) => {
            if (props.disabled) {
                return;
            }
            const grid = calculateDivisionWidth(parentRef.current?.getBoundingClientRect());
            const offsetAsPercentage = transformGridPositionToPercentage(offset / grid);
            // Update the resize position
            props.onUpdate(offsetAsPercentage, props.bounds);
        }),
        commit: useEventCallback((offset: number) => {
            if (props.disabled) {
                return;
            }
            const grid = calculateDivisionWidth(parentRef.current?.getBoundingClientRect());
            const offsetAsPercentage = transformGridPositionToPercentage(offset / grid);
            // Commit the resize action
            props.onSave(offsetAsPercentage, props.bounds);
        }),
    });

    return (
        <div
            contentEditable={false}
            className={clsx(
                'absolute top-0 bottom-0 flex w-[3px] items-center transition-opacity',
                isSelected ? 'opacity-100' : 'opacity-0',
                'group-hover/resize:opacity-100'
            )}
            style={{
                left: position,
            }}
        >
            <div
                className="group z-1"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                ref={ref}
            >
                <Tooltip
                    label={isDragging ? props.positionLabel : 'Drag to resize'}
                    position="top"
                    isOpen={isDragging || undefined}
                >
                    <Focusable>
                        <div
                            style={
                                {
                                    '--bg-default': 'var(--sp-color-bg-table-drag-button-default)',
                                    '--bg-active': 'var(--sp-color-bg-table-drag-button-active)',
                                } as React.CSSProperties
                            }
                            className={clsx(
                                '-translate-x-1/2 -translate-y-1/2 absolute top-1/2 z-20 flex cursor-col-resize items-center justify-center opacity-100 transition-all delay-75 duration-100',
                                // Default styles, a slim 'knob'
                                'h-12 max-h-full w-0.5',
                                // When knob is hovered or focused, knob expands and shows icon within
                                'group-hover:h-full group-hover:bg-[var(--sp-color-primary-300)]',
                                // When knob is selected (incl. onMouseDown)
                                'active:bg-[var(--bg-active)]',
                                isDragging
                                    ? 'h-full bg-[var(--sp-color-primary-300)]'
                                    : 'bg-[var(--bg-default)]'
                            )}
                        ></div>
                    </Focusable>
                </Tooltip>
                <div className="-translate-x-1/2 absolute top-0 bottom-0 left-0 w-4 cursor-col-resize bg-transparent opacity-100" />
            </div>
        </div>
    );
};

/**
 * Hook to handle pointer events for resizing columns in a columns layout block.
 */
function useResizer(props: {
    start: () => void;
    commit: (offset: number) => void;
    update: (offset: number) => void;
    transform: (offset: number) => number;
}): {
    ref: React.RefObject<HTMLDivElement>;
    isDragging: boolean;
    handlePointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    handlePointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
    handlePointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
} {
    const ref = React.useRef<HTMLDivElement>(null);
    const [isDragging, setDragging] = React.useState(false);
    const posX = React.useRef(0);
    return {
        ref,
        isDragging,
        handlePointerDown: useEventCallback((event: React.PointerEvent<HTMLDivElement>) => {
            if (event.button !== 0) {
                return;
            }
            event.stopPropagation();
            event.preventDefault();
            ref.current?.setPointerCapture(event.pointerId);
            setDragging(true);
            props.start();
        }),
        handlePointerUp: useEventCallback((event: React.PointerEvent<HTMLDivElement>) => {
            if (!isDragging) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            ref.current?.releasePointerCapture(event.pointerId);
            setDragging(false);
            posX.current = props.transform(event.clientX);
            // Commit the final position
            props.commit(posX.current);
        }),
        handlePointerMove: useEventCallback((event: React.PointerEvent<HTMLDivElement>) => {
            if (!isDragging) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            const lastOffset = posX.current;
            posX.current = props.transform(event.clientX);
            if (lastOffset !== posX.current) {
                props.update(posX.current);
            }
        }),
    };
}
